import * as React from "react";

import "./download-links.scss";

import {cs}        from "@Common/react/chain-services";
import {UseState}  from "@Common/react/use-state";
import {fragments} from "@Common/react/fragments";
import {Interval}  from "@Common/react/interval";
import {chain}     from "@Common/utils/fs";
import {equalDeep} from "@Common/utils/objects";

export const DownloadLinks = ({cart, next}) => cs(
    ["preparedLinks", (_, next) => UseState({initValue: [], next})],
    ["cartCounter", (_, next) => UseState({initValue: 0, next})],
    ["linkCounter", (_, next) => UseState({initValue: 0, next})],
    ["unpreparedLinks", ({preparedLinks}, next) => next(
        cart.value?.items.reduce((cartLinks, item) => {
            if (showDownloadLinks(item) && item.proofUrls) {
                cartLinks.push(...getDownloadLinks(item).flatMap(
                    ({links}) => links?.map(link => link.href)
                ).filter(
                    href => href && !preparedLinks.value.includes(href)
                ));
            }
            return cartLinks;
        }, [])
    )],
    ["reloadCart", (_, next) => !cart.loading ? next(
        cart.value.items.some(item => (showDownloadLinks(item) && !item.proofUrls)),
    ) : next(false)],
    ({reloadCart, preparedLinks, unpreparedLinks, cartCounter, linkCounter}, next) => fragments(
        reloadCart && cartCounter.value < 5 && Interval({
            delay: 5000,
            fn: async () => {
                const oldCart = cart.value;
                await cart.reload();
                if(equalDeep(oldCart, cart.value)){
                    cartCounter.onChange(cartCounter.value + 1);
                } else {
                    cartCounter.onChange(0);
                }
            },
        }),
        !!unpreparedLinks?.length && linkCounter.value < 5 && Interval({
            delay: 3000,
            startImmediately: true,
            fn: async () => {
                let confirmedLinks = [];
                for (let link of unpreparedLinks) {
                    if (await isDownloadLinkReady(link)) {
                        confirmedLinks.push(link);
                    }
                }
                if(confirmedLinks.length) {
                    linkCounter.onChange(0);
                } else {
                    linkCounter.onChange(linkCounter.value + 1);
                }
                await preparedLinks.onChange([
                    ...preparedLinks.value,
                    ...confirmedLinks,
                ]);
            },
        }),
        next()
    ),
    ({preparedLinks, cartCounter, linkCounter}) => next({
        render: (item) => cs(
            (_, next) => (
                showDownloadLinks(item) ? (
                    next()
                ) : (
                    null
                )
            ),
            () => (
                <div className="download-lines download-lines-8ie">
                    {item.proofUrls ? getDownloadLinks(item).map(({img, links}, i) => !!links?.length && (
                        <div className="download-type" key={i}>
                            <span className="download-grid" >
                                <img className="download-icon" src={img.src} alt={img.alt} />

                                {links.map((link, i) => (
                                    <div className="download-side" key={i}>

                                        {!links.some(link => !preparedLinks.value.includes(link.href)) ? (
                                            <>
                                                <div className="label">
                                                    {link.label}
                                                </div>
                                                <a {...{
                                                    href: link.href,
                                                    target: "_blank",
                                                    download: true,
                                                    className: "download",
                                                    "xd-qa-type": "icon",
                                                    "xd-qa-name": item.itemId+" " + link.label,
                                                }}>
                                                    <img src={require("../download-icon.svg")} alt="Download" className="download-svg"/>
                                                </a>
                                            </>
                                        ) : linkCounter.value < 5 ? (
                                            <div className="loading">
                                                <i className="fa fa-spinner fa-pulse"/> Validating download link(s)...
                                            </div>
                                        ) : (
                                            <div className="error">
                                                <i className="fa fa-exclamation-triangle"/> Failed to validate download link(s). Please refresh the page or go to Order History.
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </span>
                        </div>
                    )) : cartCounter.value < 5 ? (
                        <span className="loading">
                            <i className="fa fa-spinner fa-pulse"/> Loading download link(s)...
                        </span>
                    ) : (
                        <span className="error">
                            Error loading download link(s). Please refresh the page or go to Order History.
                        </span>
                    )}
                </div>
            )
        )
    }),
);

const showDownloadLinks = (item) => (
    item.selections.distributionType === "download" ||
    item.selections.options.find((opt) => opt.dev_key === "pdf_download" && opt.value)
);

const getDownloadLinks = (item) => [
    {
        img: {
            src: require("./pdf.png"),
            alt: "PDF",
        },
        links: (
            item.proofUrls.pdf?.[0]?.path && !(item.metaData?.ccReferenceURLs?.pdf?.length > 2) ? (
                item.proofUrls.pdf.map( (link, i) => ({
                    label: `PDF Side ${i+1}`,
                    href: link.path,
                }))
            ) : ([
                // {
                //     label: "PDF Download",
                //     href: `/next/web_services/get_production_pdf.php?id_order=${item.itemId}`,
                // }
            ])
        ),
    },
    {
        img: {
            src: require("./jpg-32.png"),
            alt: "JPG",
        },
        links: chain(
            item.proofUrls.jpg,
            (_) => _?.map( (link, i) => link.path && ({
                label: `JPG Side ${i+1}`,
                href: link.path,
            })),
            (_) => _?.filter(v=>v),
        ),
    },
    {
        img: {
            src: require("./zip.png"),
            alt: "ZIP",
        },
        links: chain(
            item.proofUrls.zip,
            (_) => _?.map( (link, i) => link.path && ({
                label: _.length > 1 ? `ZIP ${i+1}` : "ZIP",
                href: link.path,
            })),
            (_) => _?.filter(v=>v),
        ),
    },
];

const isDownloadLinkReady = async (url) => {
    const result = await fetch(url + "?" + new Date().getTime(), {method: 'HEAD'});
    return result.ok;
};